import { Box, EmptyContent, Flex } from '@/components/ui';
import React, { useEffect, useState } from 'react';
import { PreviewRendererProps } from '../types';
import { IconAlertCircle, IconVolume } from '@tabler/icons-react';
import { api } from '@/services/http';

const AudioPreviewRenderer: React.FC<PreviewRendererProps> = ({
  apiEndpoint,
  filename,
  mimeType,
}) => {
  const [audioBlobUrl, setAudioBlobUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(true);
  const [audioError, setAudioError] = useState<string | null>(null);

  // Check if the browser supports this audio type
  const audioElement = document.createElement('audio');
  const canPlay = mimeType ? audioElement.canPlayType(mimeType) : '';

  // Load audio with authentication
  useEffect(() => {
    let isCancelled = false;

    const loadAudio = async () => {
      if (!apiEndpoint) {
        setAudioError('No API endpoint provided');
        setAudioLoading(false);
        return;
      }

      if (!canPlay) {
        setAudioError('Audio format not supported');
        setAudioLoading(false);
        return;
      }

      try {
        const response = await api.get(apiEndpoint, {
          responseType: 'blob',
          headers: {
            Accept: 'audio/*',
          },
        });

        if (isCancelled) return;

        const blobUrl = URL.createObjectURL(response.data);
        setAudioBlobUrl(blobUrl);
        setAudioLoading(false);
      } catch {
        if (!isCancelled) {
          setAudioError('Failed to load audio');
          setAudioLoading(false);
        }
      }
    };

    loadAudio();

    return () => {
      isCancelled = true;
      if (audioBlobUrl) {
        URL.revokeObjectURL(audioBlobUrl);
      }
    };
  }, [apiEndpoint, audioBlobUrl, canPlay]);

  if (!canPlay) {
    return (
      <EmptyContent
        size="medium"
        state="warning"
        icon={<IconAlertCircle />}
        title="Audio Format Not Supported"
        description="Your browser cannot play this audio format. Please download the file to play it with an external player."
      />
    );
  }

  if (audioError) {
    return (
      <EmptyContent
        size="medium"
        state="warning"
        icon={<IconAlertCircle />}
        title="Audio Preview Error"
        description={audioError}
      />
    );
  }

  return (
    <Flex direction="column" align="center" gap="4">
      <EmptyContent
        size="medium"
        icon={<IconVolume />}
        title="Audio Player"
        description={`Playing: ${filename}`}
      />

      <Box style={{ width: '100%', maxWidth: '500px' }}>
        {audioLoading ? (
          <EmptyContent
            size="medium"
            icon={<IconVolume />}
            title="Loading Audio"
            description="Please wait while the audio loads..."
          />
        ) : (
          <audio
            src={audioBlobUrl || undefined}
            controls
            preload="metadata"
            style={{
              width: '100%',
              borderRadius: '8px',
            }}
          >
            <EmptyContent
              size="medium"
              icon={<IconVolume />}
              title="Audio Not Supported"
              description="Your browser does not support the audio element or this audio format."
            />
          </audio>
        )}
      </Box>
    </Flex>
  );
};

export default AudioPreviewRenderer;
